/**
 * Targeting Service
 *
 * Handles client-side targeting evaluation and server-side verification.
 * Provides a clean API for the popup manager to check if a popup should be shown.
 */

import type {
	TargetingGroup,
	TargetingCondition,
} from '../../shared/types/targeting';
import { evaluateTargeting, createBrowserContext } from './condition-evaluator';
import { createLogger } from '../../shared/logger';

const logger = createLogger('TargetingService');

export interface TargetingCheckResult {
	shouldShow: boolean;
	reason: string;
}

/**
 * Check if targeting conditions are met for showing a popup.
 */
export async function checkTargeting(
	groups: TargetingGroup[],
	ajaxUrl: string,
	feedId: number,
	nonce?: string
): Promise<TargetingCheckResult> {
	// No groups = show to everyone
	if (!groups || groups.length === 0) {
		return { shouldShow: true, reason: 'No targeting conditions' };
	}

	// Evaluate client-side conditions
	const context = createBrowserContext();
	const result = await evaluateTargeting(groups, context);

	// If JS evaluation failed, don't show
	if (!result.matched) {
		logger.log('Client-side targeting not matched');
		return { shouldShow: false, reason: 'Client-side conditions not met' };
	}

	// If there are pending server conditions, verify via AJAX
	if (result.pendingServerConditions.length > 0) {
		// Get nonce from global config if not provided
		const verifyNonce =
			nonce || window.gpPopupsGlobal?.verifyTargetNonce || '';

		if (!verifyNonce) {
			logger.warn('No nonce available for server condition verification');
			return {
				shouldShow: false,
				reason: 'Security token not available',
			};
		}

		logger.log(
			'Verifying server conditions:',
			result.pendingServerConditions
		);
		const serverMatched = await verifyServerConditions(
			ajaxUrl,
			feedId,
			result.pendingServerConditions,
			verifyNonce
		);
		return {
			shouldShow: serverMatched,
			reason: serverMatched
				? 'All conditions met'
				: 'Server-side conditions not met',
		};
	}

	return { shouldShow: true, reason: 'All client-side conditions met' };
}

/**
 * Verify server-side conditions via AJAX.
 */
async function verifyServerConditions(
	ajaxUrl: string,
	feedId: number,
	conditions: TargetingCondition[],
	nonce: string
): Promise<boolean> {
	try {
		const response = await fetch(ajaxUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: new URLSearchParams({
				action: 'gp_popups_verify_targeting',
				nonce,
				feed_id: String(feedId),
				conditions: JSON.stringify(conditions),
			}),
		});

		const data = await response.json();
		return data.success && data.data.matched;
	} catch (error) {
		logger.error('Failed to verify server conditions:', error);
		return false; // Fail closed
	}
}
